import { useMutation } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Alert, Button, Card, Field, Wordmark, linkTextClasses } from '@evogard/ui';
import { MIN_PASSWORD_LENGTH } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';

/**
 * "Esqueci minha senha" do COLABORADOR, e a redefinição pelo link (30/10/2026).
 *
 * Gêmeas das do portal, e separadas por construção (regra 10): outra
 * aplicação, outra sessão, outra rota. A moldura é a da tela de entrar.
 *
 * **Nenhuma das duas é mais específica que o servidor.** O pedido mostra a
 * mesma frase exista ou não o cadastro, e o link recusado não diz se venceu ou
 * já foi usado — o servidor não distingue, e a tela distinguindo reabriria do
 * lado do cliente a lista de quem trabalha na casa.
 */
export function ForgotPasswordScreen() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const request = useMutation({
    mutationFn: async () =>
      ok(
        await api.platform.recoverPassword({ body: { email: email.trim() } }),
        200,
        'Não foi possível pedir agora.',
      ),
    onError: (e: Error) => setError(e.message),
  });

  /* Enviado, o formulário some: deixá-lo convida a reenviar, e cada envio conta no limite. */
  if (request.isSuccess) {
    return (
      <Frame lead="Confira seu e-mail.">
        <Alert tone="informacao">{request.data.message}</Alert>
        <p className="text-sm text-texto-suave">
          O link vale por uma hora e só pode ser usado uma vez.
        </p>
      </Frame>
    );
  }

  function send(e: FormEvent) {
    e.preventDefault();
    setError(null);
    request.mutate();
  }

  return (
    <Frame lead="Digite o seu e-mail e enviamos um link para criar outra senha.">
      <Card>
        <form onSubmit={send} className="flex flex-col gap-4">
          {error && <Alert tone="erro">{error}</Alert>}
          <Field
            label="E-mail"
            type="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button type="submit" loading={request.isPending} disabled={!email.trim()}>
            Enviar link
          </Button>
        </form>
      </Card>
    </Frame>
  );
}

export function ResetPasswordScreen() {
  const { t: token } = useSearch({ from: '/reset-password' });
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: async () =>
      ok(
        await api.platform.resetPassword({ body: { token: token!, password } }),
        200,
        'Este link não vale mais.',
      ),
    onError: (e: Error) => setError(e.message),
  });

  if (!token) {
    return (
      <Frame lead="Criar outra senha.">
        <Alert tone="alerta" title="Falta a parte final do link.">
          Abra o link do e-mail inteiro, sem cortar. Se ele não funcionar, peça outro.
        </Alert>
      </Frame>
    );
  }

  if (reset.isSuccess) {
    return (
      <Frame lead="Senha alterada.">
        <Alert tone="sucesso">{reset.data.message}</Alert>
      </Frame>
    );
  }

  const passwordShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirmation.length > 0 && password !== confirmation;

  function send(e: FormEvent) {
    e.preventDefault();
    setError(null);
    reset.mutate();
  }

  return (
    <Frame lead="Crie a sua senha nova.">
      <Card>
        <form onSubmit={send} className="flex flex-col gap-4">
          {/* a frase é do servidor, inteira: ele não distingue vencido de usado */}
          {error && <Alert tone="alerta">{error}</Alert>}
          <Field
            label="Nova senha"
            type="password"
            required
            autoFocus
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={
              passwordShort ? `Use pelo menos ${MIN_PASSWORD_LENGTH} caracteres.` : null
            }
            help={`Pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`}
          />
          <Field
            label="Repita a senha"
            type="password"
            required
            autoComplete="new-password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            error={mismatch ? 'As duas não são iguais.' : null}
          />
          <Button
            type="submit"
            loading={reset.isPending}
            disabled={password.length < MIN_PASSWORD_LENGTH || password !== confirmation}
          >
            Salvar a senha nova
          </Button>
        </form>
      </Card>
    </Frame>
  );
}

/** A moldura da tela de entrar, com a volta para ela no pé. */
function Frame({ lead, children }: { lead: string; children: ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <div className="flex w-full max-w-sm flex-col gap-4">
        <div className="mb-2">
          <Wordmark size="capa" level="h1" subtitle="Plataforma" surface="superficie" />
          <p className="mt-3 text-sm text-texto-suave">{lead}</p>
        </div>
        {children}
        <Link to="/sign-in" className={linkTextClasses('self-start text-sm')}>
          Voltar para a entrada
        </Link>
      </div>
    </div>
  );
}
